import { Injectable } from '@angular/core';
import { BaseService } from './base.service';
import { User } from '../models/user';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  finalize,
  map,
  of,
  catchError,
  switchMap,
  throwError,
  timeout,
} from 'rxjs';
import { Login } from '../models/login';
import { AuthService } from '../shared/auth/auth.service';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { LoginResponses } from '../models/login-responses';
import { LoaderService } from './loader.service';
import { environment } from 'src/environments/environment';
import { QUIET_HTTP_ERRORS } from '../common/background-request';

interface ProfileUploadRegistration {
  userId: number;
  profileUploadPermission: string;
  profileUploadExpiresInSeconds: number;
}

export interface RegistrationWithPictureResult {
  userId: number;
  picture: 'none' | 'saved' | 'deferred';
}

@Injectable({
  providedIn: 'root',
})
export class UserService extends BaseService<User> {
  registerWithProfilePicture(user: User): Observable<RegistrationWithPictureResult> {
    // Keep the permission in this request chain only; never persist it with userData.
    const picture = user.profilePicture instanceof Blob ? user.profilePicture : null;
    return this.http.post<number | ProfileUploadRegistration>(
      `${this.apiUrl}api/tenants/membershipApp/CreateMembershipUser`, user,
      { params: { includeProfileUploadPermission: 'true' } }
    ).pipe(switchMap(registration => {
      const userId = typeof registration === 'number' ? registration : registration?.userId;
      if (!Number.isSafeInteger(userId) || userId <= 0) throw new Error('Invalid registration response.');
      if (!picture) return of<RegistrationWithPictureResult>({ userId, picture: 'none' });
      const permission = typeof registration === 'object' ? registration?.profileUploadPermission : null;
      // An older API can still create the account. Never fall back to the old
      // unauthenticated upload when that API has not issued a scoped permission.
      if (!permission) return of<RegistrationWithPictureResult>({ userId, picture: 'deferred' });
      const formData = new FormData();
      formData.append('Content', picture);
      return this.http.post<string>(
        `${this.apiUrl}api/tenants/membershipApp/userprofile/uploadFile/${userId}`, formData,
        { headers: { 'X-Profile-Upload-Permission': permission },
          context: new HttpContext().set(QUIET_HTTP_ERRORS, true) }
      ).pipe(
        map((): RegistrationWithPictureResult => ({ userId, picture: 'saved' })),
        // Registration has already succeeded. A photo failure must never cause
        // another signup request or silently report that the picture was saved.
        catchError(() => of<RegistrationWithPictureResult>({ userId, picture: 'deferred' }))
      );
    }));
  }

  updateBusinessDetails(payload: any, confirmationToken?: string): Observable<any> {
    return this.http.post(`${this.apiUrl}api/bliss/updateBlissBusiness`, payload, {
      headers: confirmationToken ? { 'X-Places-Confirm-Removal': confirmationToken } : {}
    });
  }

  private userDataSubject = new BehaviorSubject<User>(this.getUser());
  userData$ = this.userDataSubject.asObservable();
  private user: User = new User();
  private loginDetails: Login = new Login();
  personalDetails: User = new User();
  private subscription: Subscription;
  public profilePictureUrlSubject: BehaviorSubject<string> =
    new BehaviorSubject<string>('');
  private searchLocationSubject = new BehaviorSubject<
    {
      address: string;
      latitude: number;
      longitude: number;
      addressComponent?: google.maps.GeocoderAddressComponent[];
      name?: string;
    }[]
  >([]);
  searchLocation$ = this.searchLocationSubject.asObservable();
  private locationDataSubject = new BehaviorSubject<{
    address: string; latitude: number;
    longitude: number;
  }[]>([]);
  locationData$ = this.locationDataSubject.asObservable();
  readonly allowedCountries = environment.registrationAllowedCountries;
  private currentLatLngRequest?: Promise<{ lat: number; lng: number }>;
  private devicePositionRequest?: Promise<GeolocationPosition>;
  private recentLatLng?: { value: { lat: number; lng: number }; expiresAt: number };

  constructor(
    private authService: AuthService,
    private httpClient: HttpClient,
    private router: Router,
    private toastr: ToastrService,
    private loaderService: LoaderService
  ) {
    super(httpClient);
  }

  updateUser(userData: User) {
    this.user = userData;
    localStorage.setItem('userData', JSON.stringify(userData));
    this.userDataSubject.next(userData);
  }

  getUser(): User {
    const userData = localStorage.getItem('userData');
    if (this.user && this.user.email == "" && userData) {
      this.user = JSON.parse(userData);
    }

    return this.user;
  }

  setProfilePictureUrl(url: any): void {
    localStorage.setItem('ProfileUrl', url);
    this.profilePictureUrlSubject.next(url);
  }

  getProfilePictureUrl(): Observable<string> {
    return this.profilePictureUrlSubject.asObservable();
  }

  getProfilePicture(image: unknown): Observable<string> {
    // An avatar is optional display data. Do not request null filenames or make
    // an unavailable photo look like a failed sign-in. Authentication failures
    // still reach the interceptor and caller normally.
    if (typeof image !== 'string' || !image.trim()) return of('');
    return this.http.get<string>(`${this.apiUrl.replace(/\/+$/, '')}/api/tenants/me/retriveImage`, {
      params: { name: image },
      context: new HttpContext().set(QUIET_HTTP_ERRORS, true),
    }).pipe(
      timeout(10000),
      map(response => typeof response === 'string' ? response : ''),
      catchError(error => error?.status === 401 ? throwError(() => error) : of('')),
    );
  }

  loggedInUser(email: string, password: string) {
    this.loginDetails.password = password;
    this.loginDetails.email = email;

    this.subscription = this.authService
      .login(this.loginDetails)
      .subscribe((response: LoginResponses) => {
        if (response && response.access_token) {
          localStorage.setItem('token', response.access_token);
          localStorage.setItem('refreshToken', response.refresh_token);
          localStorage.setItem('expiresIn', response.expires_in);
          this.router
            .navigateByUrl('', { skipLocationChange: true })
            .then(() => {
              this.router.navigate(['']);
            });
        } else {
          this.toastr.error('Something Went Wrong!');
        }
      });
  }

  completeRegistration(user: User) {
    if (!environment.registrationApprovalRequired) {
      this.loggedInUser(user.email, user.password);
    }
  }

  getPersonalDetails(): Observable<any> {
    return this.getById(`/api/tenants/myProfile`).pipe(
      map((response: any) => {
        this.personalDetails.firstName = response.user.firstname;
        this.personalDetails.lastName = response.user.lastname;
        this.personalDetails.email = response.user.email;
        this.personalDetails.address = response.address1;
        this.personalDetails.city = response.city;
        this.personalDetails.zip = response.zip;
        this.personalDetails.mobileNumber = response.phoneNumber;
        this.personalDetails.instagramId = response.instagramId;
        this.personalDetails.tikTokId = response.tikTokId;
        this.personalDetails.faceBookId = response.facebookId;
        this.personalDetails.profilePicture = response.user.photo;
        this.personalDetails.isMembershipVendor = response.isMembershipVendor;
        this.personalDetails.allowArticleCreation = response.allowArticleCreation;
        this.personalDetails.allowPreRegistration = response.allowPreRegistration;

        return this.personalDetails;
      })
    );
  }

  setSearchLocation(
    address: string,
    latitude: number,
    longitude: number,
    addressComponent?: google.maps.GeocoderAddressComponent[],
    name?: string
  ) {
    const updatedLocations = [
      { address, latitude, longitude, addressComponent, name },
    ];
    this.searchLocationSubject.next(updatedLocations);
  }

  setProfileSelectedLocation(address: string, latitude: number, longitude: number) {
    const updatedLocations = [
      { address, latitude, longitude },
    ];
    this.locationDataSubject.next(updatedLocations);
  }

  getCurrentPosition(timeoutMs = 10000, requestPermission = true): Promise<GeolocationPosition> {
    // Device location is required for nearby discovery. The browser reuses its
    // existing grant, or requests permission when necessary. Quiet lookups are
    // reserved for optional address-picker previews, never the restaurant origin.
    if (requestPermission) return this.requestDevicePosition(timeoutMs);
    const startedAt = Date.now();
    return this.getLocationPermission().then(permission => {
      if (permission !== 'granted') throw new Error('Location permission is required.');
      const remaining = timeoutMs - (Date.now() - startedAt);
      if (remaining <= 0) throw new Error('Location request timed out.');
      return this.requestDevicePosition(remaining);
    });
  }

  private async getLocationPermission(): Promise<PermissionState | 'unknown'> {
    let deadline: ReturnType<typeof setTimeout>;
    try {
      if (!navigator.permissions?.query) return 'unknown';
      return await Promise.race([
        navigator.permissions.query({ name: 'geolocation' }).then(status => status.state),
        new Promise<'unknown'>(resolve => { deadline = setTimeout(() => resolve('unknown'), 500); })
      ]);
    } catch {
      // Older Safari/WebViews may not expose this API. Do not guess consent.
      return 'unknown';
    } finally {
      clearTimeout(deadline);
    }
  }

  private requestDevicePosition(timeoutMs: number): Promise<GeolocationPosition> {
    if (this.devicePositionRequest) return this.devicePositionRequest;
    const request = new Promise<GeolocationPosition>((resolve, reject) => {
      // The browser's timeout can exclude time spent waiting for permission.
      // Bound the whole operation too, so an unanswered prompt never stalls a page.
      const deadline = setTimeout(() => reject(new Error('Location request timed out.')), timeoutMs);
      const fail = (error: any) => { clearTimeout(deadline); reject(error); };
      if (!navigator.geolocation) {
        fail(new Error('Geolocation is not supported by this browser.'));
        return;
      }
      try {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            clearTimeout(deadline);
            const lat = position?.coords?.latitude, lng = position?.coords?.longitude;
            if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
              reject(new Error('Location lookup returned no coordinates.'));
              return;
            }
            resolve(position);
          },
          fail,
          { timeout: timeoutMs, maximumAge: 300000, enableHighAccuracy: false }
        );
      } catch (error) { fail(error); }
    }).then(position => {
      this.recentLatLng = { value: { lat: position.coords.latitude, lng: position.coords.longitude }, expiresAt: Date.now() + 300000 };
      return position;
    });
    this.devicePositionRequest = request.finally(() => { this.devicePositionRequest = undefined; });
    return this.devicePositionRequest;
  }

  getCurrentLocation(): Promise<{ city: string, lat: number, lng: number }> {
    return this.getCurrentPosition().then(
      (position) => {
        return this.getLocationName(position.coords.latitude, position.coords.longitude);
      },
      (error) => {
        console.error('Error getting location', error);
        throw error;
      }
    );
  }

  getLocationName(lat: number, lng: number): Promise<{ city: string, lat: number, lng: number }> {
    return new Promise((resolve, reject) => {
      const geocoder = new google.maps.Geocoder();
      const latlng = new google.maps.LatLng(lat, lng);

      geocoder.geocode({ location: latlng }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK) {
          if (results[0]) {
            for (let component of results[0].address_components) {
              if (component.types.includes('locality')) {
                const city = component.long_name;
                resolve({ city, lat, lng });
                return;
              }
            }
            reject('Locality not found in address components');
          } else {
            reject('No results found');
          }
        } else {
          reject('Geocoder failed due to: ' + status);
        }
      });
    });
  }

  async getUserCountry(): Promise<boolean> {
    if (!environment.registrationGeoRestrictionEnabled) {
      return true;
    }

    try {
      const country = await this.getCurrentPosition(10000, true).then(
        (position) => {
          const { latitude, longitude } = position.coords;
          return this.getCountryFromGoogleMaps(latitude, longitude);
        },
        () => this.getCountryByIP()
      );

      return this.allowedCountries.includes(country);
    } catch {
      // Live registration is intentionally fail-closed when neither browser nor IP
      // geolocation can establish that the user is in an allowed country.
      return false;
    }
  }

  getCountryFromGoogleMaps(lat: number, lng: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const geocoder = new google.maps.Geocoder();
      const latlng = new google.maps.LatLng(lat, lng);
      geocoder.geocode({ location: latlng }, (results: any[], status: string) => {
        if (status === google.maps.GeocoderStatus.OK) {
          for (const result of results) {
            const countryComponent = result.address_components.find((comp: any) =>
              comp.types.includes('country')
            );
            if (countryComponent) {
              resolve(countryComponent.long_name);
              return;
            }
          }
          reject('Country not found');
        } else {
          reject('Geocoder failed due to: ' + status);
        }
      });
    });
  }

  async getCountryByIP(): Promise<string> {
    const res = await fetch('https://ipapi.co/json');
    const data = await res.json();
    return data.country_name;
  }

  async getLocationByIP(): Promise<any> {
    const controller = new AbortController();
    let deadline: ReturnType<typeof setTimeout>;
    try {
      return await Promise.race([
        (async () => {
          const res = await fetch('https://ipapi.co/json', { signal: controller.signal });
          if (!res.ok) throw new Error('Location lookup unavailable.');
          const data = await res.json();
          if (!Number.isFinite(data.latitude) || !Number.isFinite(data.longitude)
            || Math.abs(data.latitude) > 90 || Math.abs(data.longitude) > 180) {
            throw new Error('Location lookup returned no coordinates.');
          }
          return { lat: data.latitude, lng: data.longitude };
        })(),
        new Promise<never>((_, reject) => {
          deadline = setTimeout(() => {
            controller.abort();
            reject(new Error('Location lookup timed out.'));
          }, 3000);
        })
      ]);
    } finally {
      clearTimeout(deadline);
    }
  }

  async getCityByIP(): Promise<any> {
    const res = await fetch('https://ipapi.co/json');
    const data = await res.json();
    return data.city;
  }

  getCurrentLatLng(): Promise<{ lat: number; lng: number }> {
    if (this.recentLatLng && this.recentLatLng.expiresAt > Date.now()) {
      return Promise.resolve(this.recentLatLng.value);
    }
    // Parent/list/map mount together: share their lookup and reuse it on navigation.
    if (!this.currentLatLngRequest) {
      this.currentLatLngRequest = this.getCurrentPosition(10000, true)
        .then(position => ({ lat: position.coords.latitude, lng: position.coords.longitude }))
        .then(value => {
          // This cache contains device coordinates only. IP lookup must never
          // silently substitute a different origin when permission/GPS fails.
          this.recentLatLng = { value, expiresAt: Date.now() + 300000 };
          return value;
        })
        .finally(() => { this.currentLatLngRequest = undefined; });
    }
    return this.currentLatLngRequest;
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private foodieOfferList = [
    {
      id: 1,
      title: "Thursday Salad",
      type: "deal",
      description: "Thursday special deal salad $2.9",
      time: "9 AM - 11 PM"
    },
    {
      id: 2,
      title: "Pasta Special",
      type: "offer",
      description: "Thursday special deal salad $2.9",
      time: "9 AM - 11 PM"
    },
    {
      id: 3,
      title: "Cocktails",
      type: "deal",
      description: "Thursday special deal salad $2.9",
      time: "9 AM - 11 PM"
    },
    {
      id: 4,
      title: "Thursday Salad",
      type: "deal",
      description: "Thursday special deal salad $2.9",
      time: "9 AM - 11 PM"
    },
    {
      id: 5,
      title: "Pasta Special",
      type: "offer",
      description: "Thursday special deal salad $2.9",
      time: "9 AM - 11 PM"
    },
    {
      id: 6,
      title: "Cocktails",
      type: "deal",
      description: "Thursday special deal salad $2.9",
      time: "9 AM - 11 PM"
    },
    {
      id: 7,
      title: "Thursday Salad",
      type: "deal",
      description: "Thursday special deal salad $2.9",
      time: "9 AM - 11 PM"
    },
    {
      id: 8,
      title: "Pasta Special",
      type: "offer",
      description: "Thursday special deal salad $2.9",
      time: "9 AM - 11 PM"
    },
    {
      id: 9,
      title: "Cocktails",
      type: "deal",
      description: "Thursday special deal salad $2.9",
      time: "9 AM - 11 PM"
    }
  ];

  private foodieNewsList = [
    {
      id: 1,
      title: "I don't need to drink",
      tags: ["Drinks"],
      date: "2024-04-24T10:00:15.553Z",
      imagePath: "./assets/images/food-news-1.jpg"
    },
    {
      id: 2,
      title: "Bow ties are cool!",
      tags: ["Drinks", "Breakfast"],
      date: "2024-04-24T10:00:15.553Z",
      imagePath: "./assets/images/food-news-2.jpg"
    },
    {
      id: 3,
      title: "Yo Yogurt!",
      tags: ["Drinks", "Breakfast", "Healthy"],
      date: "2024-04-24T10:00:15.553Z",
      imagePath: "./assets/images/food-news-3.jpg"
    },
    {
      id: 4,
      title: "Some good news here",
      tags: ["Drinks", "Healthy"],
      date: "2024-04-24T10:00:15.553Z",
      imagePath: "./assets/images/food-news-4.jpg"
    },
    {
      id: 5,
      title: "Bow ties are cool!",
      tags: ["Healthy"],
      date: "2024-04-24T10:00:15.553Z",
      imagePath: "./assets/images/food-news-1.jpg"
    },
    {
      id: 6,
      title: "Yo Yogurt!",
      tags: ["Drinks", "Healthy"],
      date: "2024-04-24T10:00:15.553Z",
      imagePath: "./assets/images/food-news-2.jpg"
    }
  ];

  getFoodieNewsData(): Observable<any> {
    return of(this.foodieNewsList);
  }

  getFoodieOffersData(): Observable<any> {
    return of(this.foodieOfferList);
  }
}
